import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

/**
 * 管理者向け AI 設定プリセット API（master_admin 専用）。FE `/admin/ai` ページ用。
 *
 * グローバル設定（プロバイダ/モデル/パラメータ）とシーン別プロンプトを
 * 「名前付きプリセット」として複数保存し、アクティブなものを切り替える。
 *
 * - `GET   /api/admin/ai/overview` 全体フラグ + プリセット一覧 + シーン一覧
 * - `PATCH /api/admin/ai/setting` AI 機能全体の有効/無効
 * - `POST/PATCH/DELETE /api/admin/ai/presets(/:id)` グローバル設定プリセット CRUD
 * - `POST  /api/admin/ai/presets/:id/activate` アクティブ切替（全体で 1 件）
 * - `POST  /api/admin/ai/scenes/:key/presets` シーンプリセット作成
 * - `PATCH/DELETE /api/admin/ai/scene-presets/:id` シーンプリセット編集 / 削除
 * - `POST  /api/admin/ai/scene-presets/:id/activate` アクティブ切替（シーン内で 1 件）
 *
 * activate は body `{ active: false }` で解除でき、実行時は従来の設定
 * （Django admin の AISetting / AITextScene → コードデフォルト）へ戻る。
 * `api_key` はレスポンスに含まれず `has_api_key`（bool）のみ返る。編集時に
 * `api_key` を省略すると変更なし、空文字を送るとクリアされる。
 */

export type AiProvider = "openai" | "anthropic" | "gemini";

/** プロバイダ値 → 表示名（一覧・フォームで共用）。 */
export const AI_PROVIDER_LABELS: Record<AiProvider, string> = {
  openai: "OpenAI",
  anthropic: "Anthropic (Claude)",
  gemini: "Google Gemini",
};

export interface AiSettingPreset {
  id: number;
  name: string;
  is_active: boolean;
  provider: AiProvider;
  model_name: string;
  api_base: string;
  has_api_key: boolean;
  temperature: number;
  max_tokens: number;
  timeout: number;
  max_retries: number;
  modified: string;
}

export interface AiScenePreset {
  id: number;
  scene_key: string;
  name: string;
  is_active: boolean;
  system_prompt: string;
  rules: string;
  history_limit: number;
  modified: string;
}

/** シーン種別（BE の SceneSpec.kind と対応）。text=テキスト生成 / image=画像生成 */
export type AiSceneKind = "text" | "image";

export interface AiScene {
  key: string;
  display_name: string;
  kind: AiSceneKind;
  is_enabled: boolean;
  default_system_prompt: string;
  default_rules: string;
  default_history_limit: number;
  presets: AiScenePreset[];
}

export interface AiAdminOverviewResponse {
  status: string;
  data: {
    is_enabled: boolean;
    setting_presets: AiSettingPreset[];
    scenes: AiScene[];
  };
}

export interface AiSettingPresetInput {
  name?: string;
  provider?: AiProvider;
  model_name?: string;
  api_base?: string;
  /** 省略で変更なし、空文字でクリア。 */
  api_key?: string;
  temperature?: number;
  max_tokens?: number;
  timeout?: number;
  max_retries?: number;
}

export interface AiScenePresetInput {
  name?: string;
  system_prompt?: string;
  rules?: string;
  history_limit?: number;
}

interface SettingPresetResponse {
  status: string;
  data: AiSettingPreset;
}

/** 接続テストの結果。テストが実行できれば成否に関わらず 200 で返る。 */
export interface SettingPresetTestResult {
  ok: boolean;
  /** 管理者向けメッセージ（失敗時は原因。API キーは伏せ字） */
  message: string;
  elapsed_ms: number;
}

interface SettingPresetTestResponse {
  status: string;
  data: SettingPresetTestResult;
}

interface ScenePresetResponse {
  status: string;
  data: AiScenePreset;
}

export const adminAiApi = createApi({
  reducerPath: "adminAiApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["AdminAiOverview"],
  endpoints: (builder) => ({
    getAiOverview: builder.query<AiAdminOverviewResponse, void>({
      query: () => `/api/admin/ai/overview`,
      providesTags: ["AdminAiOverview"],
    }),

    updateAiSetting: builder.mutation<
      { status: string; data: { is_enabled: boolean } },
      { is_enabled: boolean }
    >({
      query: (body) => ({
        url: `/api/admin/ai/setting`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["AdminAiOverview"],
    }),

    // ===== グローバル設定プリセット =====
    createSettingPreset: builder.mutation<
      SettingPresetResponse,
      AiSettingPresetInput
    >({
      query: (body) => ({
        url: `/api/admin/ai/presets`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminAiOverview"],
    }),

    updateSettingPreset: builder.mutation<
      SettingPresetResponse,
      { id: number; body: AiSettingPresetInput }
    >({
      query: ({ id, body }) => ({
        url: `/api/admin/ai/presets/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["AdminAiOverview"],
    }),

    deleteSettingPreset: builder.mutation<{ status: string }, number>({
      query: (id) => ({
        url: `/api/admin/ai/presets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminAiOverview"],
    }),

    activateSettingPreset: builder.mutation<
      SettingPresetResponse,
      { id: number; active?: boolean }
    >({
      query: ({ id, active = true }) => ({
        url: `/api/admin/ai/presets/${id}/activate`,
        method: "POST",
        body: { active },
      }),
      invalidatesTags: ["AdminAiOverview"],
    }),

    // 保存済みプリセットの設定で 1 回だけ生成を実行する（有効化前の確認用）。
    testSettingPreset: builder.mutation<SettingPresetTestResponse, number>({
      query: (id) => ({
        url: `/api/admin/ai/presets/${id}/test`,
        method: "POST",
      }),
    }),

    // ===== シーン別プロンプトプリセット =====
    createScenePreset: builder.mutation<
      ScenePresetResponse,
      { sceneKey: string; body: AiScenePresetInput }
    >({
      query: ({ sceneKey, body }) => ({
        url: `/api/admin/ai/scenes/${sceneKey}/presets`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdminAiOverview"],
    }),

    updateScenePreset: builder.mutation<
      ScenePresetResponse,
      { id: number; body: AiScenePresetInput }
    >({
      query: ({ id, body }) => ({
        url: `/api/admin/ai/scene-presets/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["AdminAiOverview"],
    }),

    deleteScenePreset: builder.mutation<{ status: string }, number>({
      query: (id) => ({
        url: `/api/admin/ai/scene-presets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdminAiOverview"],
    }),

    activateScenePreset: builder.mutation<
      ScenePresetResponse,
      { id: number; active?: boolean }
    >({
      query: ({ id, active = true }) => ({
        url: `/api/admin/ai/scene-presets/${id}/activate`,
        method: "POST",
        body: { active },
      }),
      invalidatesTags: ["AdminAiOverview"],
    }),
  }),
});

export const {
  useGetAiOverviewQuery,
  useUpdateAiSettingMutation,
  useCreateSettingPresetMutation,
  useUpdateSettingPresetMutation,
  useDeleteSettingPresetMutation,
  useActivateSettingPresetMutation,
  useTestSettingPresetMutation,
  useCreateScenePresetMutation,
  useUpdateScenePresetMutation,
  useDeleteScenePresetMutation,
  useActivateScenePresetMutation,
} = adminAiApi;
